import React from "react";
import { motion } from "framer-motion";
import {
  Users,
  CheckCircle,
  Globe,
  Award,
  Mail,
  Star,
  Target,
  Leaf,
  Shield,
  FileCheck,
  Building2,
  Scale,
} from "lucide-react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const WRAP_Certification_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#8b4513] via-[#a0522d] to-[#cd853f] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-400 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-amber-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <Users size={18} className="text-orange-300" />
            <span>Responsible Manufacturing</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-orange-300 via-amber-300 to-yellow-300">
            WRAP Certification
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
            Worldwide Responsible Accredited Production - Ethical Manufacturing
            Standards
          </p>
        </motion.div>
      </section>

      {/* What is WRAP */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            What Is WRAP Certification?
          </h2>
          <div className="space-y-6 text-lg text-gray-700">
            <p>
              WRAP (Worldwide Responsible Accredited Production) is a global
              certification program dedicated to promoting ethical and socially
              responsible manufacturing practices across the supply chain. WRAP
              certification ensures compliance with the highest standards of
              labor rights, health and safety, environmental sustainability, and
              ethical business practices.
            </p>
            <p>
              WRAP certification is recognized as one of the most comprehensive
              and respected social compliance certifications in the apparel and
              textile industries, helping businesses demonstrate their
              commitment to corporate social responsibility (CSR) and ethical
              sourcing globally.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            Key Features of WRAP Certification
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: "Fair Labor Practices",
                desc: "Ensures fair wages, working hours, and international labor standards",
                icon: Scale,
              },
              {
                title: "Health and Safety Standards",
                desc: "Maintains safe working environments with protective equipment",
                icon: Shield,
              },
              {
                title: "Environmental Responsibility",
                desc: "Encourages sustainable practices and waste management",
                icon: Leaf,
              },
              {
                title: "Ethical Business Practices",
                desc: "Ensures compliance with anti-corruption and fair trade laws",
                icon: Award,
              },
              {
                title: "Worker Rights Protection",
                desc: "Prevents forced labor, child labor, and discrimination",
                icon: Users,
              },
              {
                title: "Ongoing Compliance Monitoring",
                desc: "Regular audits ensure continuous standards adherence",
                icon: FileCheck,
              },
            ].map((feature, i) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
                >
                  <IconComponent className="w-10 h-10 text-orange-600 mb-4" />
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">
                    {feature.title}
                  </h3>
                  <p className="text-gray-700">{feature.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </section>

      {/* WRAP's 12 Principles */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            What Is WRAP's 12 Principles?
          </h2>
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-8 rounded-lg">
            <p className="text-lg text-gray-700 mb-6">
              The WRAP Certification Standards are based on 12 key principles
              reflecting international labor laws and human rights:
            </p>
            <ul className="space-y-3">
              {[
                "Compliance with Laws and Workplace Regulations",
                "Prohibition of Forced Labor",
                "Prohibition of Child Labor",
                "Prohibition of Harassment and Abuse",
                "Compensation and Benefits",
                "Hours of Work",
                "Health and Safety",
                "Freedom of Association",
                "Non-Discrimination",
                "Prohibition of Retaliation",
                "Environment",
                "Customs Compliance",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-orange-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </section>

      {/* Importance */}
      <section className="py-16 px-4 bg-gray-50">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            Why WRAP Certification Matters
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Social Responsibility - Demonstrate commitment to fair practices",
              "Market Access - Required by retailers and global buyers",
              "Supplier Accountability - Ensure ethical supply chain",
              "Enhanced Brand Reputation - Build consumer trust",
              "Regulatory Compliance - Meet international labor laws",
              "Business Protection - Reduce risks and liability",
            ].map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-start gap-4"
              >
                <Star className="w-6 h-6 text-orange-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700 text-lg">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Benefits of WRAP Certification
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Improved Supply Chain Responsibility",
              "Access to Retailers and global markets",
              "Worker Welfare improvements",
              "Environmental Sustainability practices",
              "Brand Reputation and consumer trust",
              "Regulatory Compliance assurance",
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.1 }}
                className="bg-gradient-to-br from-orange-100 to-amber-100 p-6 rounded-lg"
              >
                <CheckCircle className="w-6 h-6 text-orange-700 mb-3" />
                <p className="text-gray-800 font-medium">{item}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Advantages */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Advantages of WRAP Certification
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Supply Chain Responsibility and transparency",
              "Global Market Access and buyer requirements",
              "Worker Welfare and human rights commitment",
              "Environmental Sustainability improvements",
              "Brand Reputation and consumer loyalty",
              "Legal Compliance with labor standards",
              "Supplier Relationship Improvement",
              "Competitive Market Advantage",
            ].map((advantage, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-start gap-3 p-4 bg-white rounded-lg"
              >
                <CheckCircle className="w-5 h-5 text-orange-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{advantage}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Industries */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Industries Using WRAP Certification
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Apparel and Clothing Manufacturing",
              "Textile Production",
              "Fashion Retailers",
              "Supply Chain Management",
              "Electronics Manufacturing",
              "Footwear Production",
            ].map((industry, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-center gap-3 p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg"
              >
                <Building2 className="w-5 h-5 text-orange-600 flex-shrink-0" />
                <span className="text-gray-700 font-medium">{industry}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* How to Achieve */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            How to Achieve WRAP Certification
          </h2>
          <div className="space-y-6">
            {[
              {
                step: 1,
                title: "Join WRAP Program",
                desc: "Register your company with WRAP",
              },
              {
                step: 2,
                title: "Self-Assessment",
                desc: "Complete self-assessment against WRAP's 12 Principles",
              },
              {
                step: 3,
                title: "Supplier and Facility Audits",
                desc: "Undergo WRAP audit of manufacturing facilities",
              },
              {
                step: 4,
                title: "Develop Action Plans",
                desc: "Create plans to address non-compliance issues",
              },
              {
                step: 5,
                title: "Submit Documentation",
                desc: "Submit verification documentation to WRAP",
              },
              {
                step: 6,
                title: "Continuous Monitoring",
                desc: "Maintain compliance through regular reviews and audits",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.06 }}
                className="flex gap-6 items-start"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                  {item.step}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-700">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-r from-orange-50 via-amber-50 to-yellow-50">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900 text-center">
            Why Choose MBA Solutions for WRAP Certification?
          </h2>
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <p className="text-lg text-gray-700 mb-6">
              MBA Solutions provides expert WRAP consultancy services, guiding
              manufacturers through the WRAP certification process to ensure
              compliance with 12 ethical principles and responsible production
              standards.
            </p>
            <ul className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                "WRAP Self-Assessment Support",
                "Audit Preparation and Gap Analysis",
                "Action Plan Development",
                "Employee Training Services",
                "Compliance Verification",
                "Ongoing Monitoring Support",
              ].map((service, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-orange-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{service}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-700 mb-6">
              We help manufacturers achieve WRAP certification ensuring ethical
              labor practices, worker welfare, and socially responsible
              production throughout the supply chain.
            </p>
            <div className="border-t pt-6 mt-6">
              <p className="text-gray-800 font-semibold mb-4">Get in Touch:</p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-orange-600" />
                  <span className="text-gray-700">
                    <strong>Email:</strong> atif@mbasolutions.co
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-orange-600" />
                  <span className="text-gray-700">
                    <strong>Website:</strong> www.mbasolutions.co
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      <Footer />
    </>
  );
};

export default WRAP_Certification_Details;
